import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from "@angular/forms";

export interface PageEvent {
  page: number;
  pageSize: number;
}

@Component({
  selector: 'app-paginator',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './paginator.component.html',
  styleUrl: './paginator.component.css'
})
export class PaginatorComponent<T> implements OnChanges {

  // ── Common inputs ────────────────────────────────────────────
  @Input() pageSize: number = 10;
  @Input() pageSizeOptions: number[] = [5, 10, 20, 50];
  /** Allows a parent to restore the current page after this component is recreated. */
  @Input() set activePage(val: number) { if (val >= 1) this.page = val; }

  // ── Client-side only ─────────────────────────────────────────
  @Input() dataSource: T[] = [];

  // ── Server-side only ─────────────────────────────────────────
  @Input() serverSide: boolean = false;
  @Input() totalItems: number = 0;          // must provide when serverSide = true

  // ── Outputs ──────────────────────────────────────────────────
  @Output() pageChange = new EventEmitter<T[]>();           // client-side
  @Output() pageChangeServer = new EventEmitter<PageEvent>(); // server-side

  page: number = 1;

  ngOnChanges(changes: SimpleChanges): void {
    if (this.serverSide) {
      // Reset page if pageSize changed externally
      if (changes['pageSize'] && !changes['pageSize'].firstChange) {
        this.page = 1;
      }
      // Reset page if totalItems changed and current page is now out of range
      if (changes['totalItems'] && !changes['totalItems'].firstChange) {
        if (this.page > this.totalPages) {
          this.page = 1;
        }
      }
    } else {
      // Client-side: reset page when data or pageSize changes
      if (changes['dataSource']) this.page = 1;
      if (changes['pageSize']) this.page = 1;

      setTimeout(() => this.pageChange.emit(this.paginatedData), 0);
    }
  }


  // ── Computed ─────────────────────────────────────────────────

  get total(): number {
    return this.serverSide
      ? this.totalItems                  // trust the backend count
      : this.totalItems = (this.dataSource?.length || 0);  // count locally
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  get startIndex(): number {
    return this.total === 0 ? 0 : (this.page - 1) * this.pageSize;
  }

  get endIndex(): number {
    return this.total === 0 ? 0 : Math.min(this.startIndex + this.pageSize, this.total);
  }

  get paginatedData(): T[] {
    return this.dataSource?.slice(this.startIndex, this.endIndex) || [];
  }

  // ── Actions ──────────────────────────────────────────────────

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.page) {
      this.page = page;
      this.emit();
    }
  }

  onPageSizeChange(): void {
    this.page = 1;
    this.emit();
  }

  private emit(): void {
    if (this.serverSide) {
      this.pageChangeServer.emit({ page: this.page, pageSize: this.pageSize });
    } else {
      this.pageChange.emit(this.paginatedData);
    }
  }
}